import React from "react";

import * as graphics from "@client/app/graphics";
import * as constants from "@client/constants";
import * as analytics from "@client/shared/analytics";
import {zIndexClasses} from "@client/style";

import {Icon} from "@client/components/Icon";
import {Image} from "@client/components/Image";
import {NoOutstandingRequestsIndicator} from "@client/components/NoOutstandingRequestsIndicator";
import {Shell} from "@client/components/Shell";
import {Notifications} from "@client/shared/components/Notifications";
import {Static} from "@client/shared/components/Static";
import {FooterBlock} from "@client/site/components/FooterBlock";
import {useMessages, useSiteContext, useStaticURL} from "@client/site/context";
import * as forms from "@client/site/forms";
import {classNames, reverse} from "@reactivated";

export const layoutStyles = {
    pageIsLoaded: "!hidden",
    background: classNames(
        "site-background",
        `h-[650px] -top-[100px] absolute w-full ${zIndexClasses.siteBackground} left-0`,
        "skew-y-[4deg]",
    ),
    nav: classNames(
        "grid",
        "grid-rows-[auto_auto]",
        "md:px-2.5 md:grid-cols-2 md:grid-rows-none md:py-2.5",
    ),
    bar: classNames(
        "relative grid",
        "px-2.5 py-2.5 border-b border-[var(--menu-border-color)]",
        "md:px-0 md:py-0 md:border-b-0",
    ),
    logo: classNames("site-logo", "block my-auto"),
    mobileMenuHandler: classNames(
        "absolute h-full top-0 right-0 p-[inherit]",
        "md:hidden",
    ),
    menus: classNames(
        "menus",
        "hidden font-medium",
        "flex-col",
        "md:flex md:flex-row md:ml-auto",
    ),
    isMenuOpen: "flex",
    content: classNames("px-2.5 flex-1 pt-2.5 pb-5"),
    center: "flex-none justify-evenly flex flex-col grow",
    narrow: "narrow",
    liquid: "liquid",
    footer: classNames(
        "site-footer",
        "px-2.5 relative pt-[13px] pb-[13px] bg-[#F7F9FA]",
        "grid grid-cols-[repeat(12,1fr)] gap-5",
    ),
    socialAndCopyright: "space-y-[10px]",
    footerLogo: classNames(
        "site-footer-logo",
        "block ml-auto w-[35px] h-[12px]",
        "max-md:mr-auto",
    ),
    copyright: "text-xs font-medium",
    social: classNames("site-social", "space-x-[10px] flex"),
    menu: classNames(
        "site-menu",
        "flex items-center whitespace-nowrap text-[15px]",
        "flex-col",
        "md:flex-row",
    ),
    footerBlock: "space-y-[10px] my-auto text-xs whitespace-nowrap",
    footerHeader: "uppercase font-medium",
    footerLinks: "space-y-[10px]",
    register: classNames("site-register", "space-x-[10px] flex items-center"),
    highlight: classNames(
        "site-highlight",
        "p-2.5 text-[length:var(--text-size)] leading-[1.3]",
    ),
    highlightBorder: "border-t-2 border-[var(--light-color)]",
    ResetPassword: classNames("site-reset-password", "flex flex-col gap-[15px]"),
};

export type LayoutStyles = typeof layoutStyles;

const FOOTER_BLOCKS = [
    {
        title: "Navigation",
        links: [
            // {to: reverse("post_list"), title: "Blog"},
            {to: reverse("business_page"), title: "Joy for Business"},
        ],
    },
    {
        title: "Legal",
        links: [
            {to: reverse("privacy_policy"), title: "Privacy Policy"},
            {to: reverse("terms_of_service"), title: "Terms of Service"},
        ],
    },
    {
        title: "Download",
        links: [
            {to: constants.APP_STORE_URL, title: "App Store"},
            {to: constants.PLAY_STORE_URL, title: "Play Store"},
        ],
    },
] as const;

export interface Props {
    title: string | {page: string; meta: string};
    narrow?: boolean | "liquid";
    center?: boolean;
    children: React.ReactNode;
    metaImage?: string;
    background?: React.ReactNode;
    isBusiness?: true;
}

export const Layout = (props: Props) => {
    const context = useSiteContext();
    const staticURL = useStaticURL();
    const {messages} = useMessages();
    const [isMenuOpen, setMenuOpen] = React.useState(false);

    analytics.useTrackPageView();

    const {title, metaTitle} =
        typeof props.title === "object"
            ? {title: props.title.page, metaTitle: props.title.meta}
            : {title: props.title, metaTitle: props.title};
    const metaImage = props.metaImage ?? `${staticURL}images/og-default.png`;

    const background = props.background ?? (
        <div className={layoutStyles.background}>
            <Image image="background" title={null} />
        </div>
    );

    const menuBorderColor = (
        props.isBusiness
            ? {"--menu-border-color": "transparent"}
            : {"--menu-border-color": "#D8D8D8"}
    ) as React.CSSProperties;

    return (
        <Shell
            bodyClassName={props.narrow == "liquid" ? layoutStyles.narrow : undefined}
        >
            <div
                className="m-auto flex flex-1 flex-col"
                style={{maxWidth: constants.SITE_MAX_WIDTH}}
            >
                <Static />
                <meta charSet="utf-8" />
                <title>{title}</title>
                <meta
                    name="viewport"
                    content="width=device-width, initial-scale=1, shrink-to-fit=no"
                />
                <link rel="icon" href={`${staticURL}favicon.png`} type="image/png" />
                <meta property="og:title" content={metaTitle} />
                <meta property="og:image" content={metaImage} />
                {props.narrow === "liquid" && <body className={layoutStyles.liquid} />}
                {background}
                <NoOutstandingRequestsIndicator />
                <Icon icon="info-circle" className={layoutStyles.pageIsLoaded} />
                <nav className={layoutStyles.nav} style={menuBorderColor}>
                    <div className={layoutStyles.bar}>
                        <a className={layoutStyles.logo} href={reverse("home_page")}>
                            <graphics.Logo title="Joy" />
                        </a>
                        <button
                            className={layoutStyles.mobileMenuHandler}
                            onClick={() => setMenuOpen(!isMenuOpen)}
                        >
                            <Icon large icon="bars" />
                        </button>
                    </div>
                    <div
                        className={classNames(
                            layoutStyles.menus,
                            isMenuOpen && layoutStyles.isMenuOpen,
                        )}
                    >
                        <ul className={layoutStyles.menu}>
                            {props.isBusiness !== true && (
                                <li>
                                    <a href={reverse("calculator")}>Calculator</a>
                                </li>
                            )}
                            <li>
                                <a href={reverse("contact")}>Contact</a>
                            </li>
                        </ul>
                        <ul className={layoutStyles.menu}>
                            {context.profile == null ? (
                                <>
                                    {props.isBusiness === true ? (
                                        <li>
                                            <a href={reverse("manager_registration")}>
                                                Get Started
                                            </a>
                                        </li>
                                    ) : (
                                        <li>
                                            <a href={reverse("register")}>Sign up</a>
                                        </li>
                                    )}
                                    <li>
                                        <a href={reverse("login")}>Login</a>
                                    </li>
                                </>
                            ) : (
                                <>
                                    <li>
                                        <a href={reverse("journal")}>Journal</a>
                                    </li>
                                    {context.profile.user.is_manager === true && (
                                        <li>
                                            <a href={reverse("clients_list")}>
                                                My Clients
                                            </a>
                                        </li>
                                    )}
                                    <li>
                                        <a href={reverse("profile")}>My Account</a>
                                    </li>
                                    <li>
                                        <form
                                            action={reverse("rpc_logout")}
                                            method="POST"
                                        >
                                            <forms.CSRFToken />
                                            <button
                                                onClick={() => analytics.logout("site")}
                                            >
                                                Logout
                                            </button>
                                        </form>
                                    </li>
                                </>
                            )}
                        </ul>
                    </div>
                </nav>
                <Notifications position="site" notifications={messages} />
                <div
                    className={classNames(
                        layoutStyles.content,
                        props.narrow === true && layoutStyles.narrow,
                        props.center === true && layoutStyles.center,
                    )}
                >
                    {props.children}
                </div>
                <div className={layoutStyles.footer}>
                    {FOOTER_BLOCKS.map((block, index) => (
                        <FooterBlock
                            key={index}
                            title={block.title}
                            links={block.links}
                        />
                    ))}
                    <div className={layoutStyles.socialAndCopyright}>
                        <graphics.Logo
                            className={layoutStyles.footerLogo}
                            title="Joy"
                        />
                        <div className={layoutStyles.copyright}>
                            &copy; {new Date().getFullYear()} Joy Network LLC
                        </div>
                        <div className={layoutStyles.social}>
                            <a href={constants.FACEBOOK_URL}>
                                <Icon large icon={"custom-facebook"} />
                            </a>
                            <a href={constants.INSTAGRAM_URL}>
                                <Icon large icon={"custom-instagram"} />
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </Shell>
    );
};
